import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FavoriteSchedule } from '@ridango/components/lib/favorites/favorites.model';
import { FavoritesFacade } from '@ridango/components/lib/favorites/+state/favorites.facade';
import Env from '@ridango/core/env';
import { combineLatest, forkJoin } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
    ApplicationFacade,
    DetailedRoute,
    DetailedStop,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    OtpUtil,
    Pattern,
    ShapeType,
    Time
} from '@ridango/components';
import moment from 'moment';
import { BaseComponent, DepartureArrivalTime, LatLng, NetworkService, BackNavigationService } from '@ridango/core';
import { PrintScheduleService } from '../../../services/print-schedule.service';
import { MapApiService } from '../../map/services/map-api.service';
import { takeUntil } from 'rxjs/operators';
import { PatternScheduleSharedService } from '../../../services/pattern-schedule-shared.service';

enum TabType {
    TODAY = 'today',
    TOMORROW = 'tomorrow',
    DATE = 'date'
}

enum ActionType {
    ADDITIONAL_INFO = 'additionalInfo',
    SAVE_AS_FAVORITE = 'saveAsFavorite',
    REMOVE_FROM_FAVORITES = 'removeFromFavorites',
    PRINT_SCHEDULE = 'printSchedule'
}

@Component({
    selector: 'rwidget-pattern-schedule-on-stop',
    templateUrl: './pattern-schedule-on-stop.component.html',
    styleUrls: ['./pattern-schedule-on-stop.component.scss'],
    providers: [BackNavigationService]
})
export class PatternScheduleOnStopComponent extends BaseComponent implements OnInit, OnDestroy {
    TabTypeEnum = TabType;
    assetsPath$ = this.applicationFacade.assetsPath$;
    tabType: TabType = TabType.TODAY;
    scheduleRoute: DetailedRoute;
    schedulePattern: Pattern;
    schedulePatternTitle = '';
    scheduleStop: DetailedStop;
    setDateValue: DepartureArrivalTime;
    times: Time[] = [];
    params: { patternId: string; stopId: string };
    ShapeType = ShapeType;
    favoriteSchedules$ = this.favoritesFacade.favoriteSchedules$;
    allFavoriteSchedules = [];

    actions: { title: string; type: ActionType }[];
    favoriteStopSchedule: FavoriteSchedule;
    networkStatus$ = this.network.getStatus();
    hostname = window.location.hostname;

    constructor(
        private activatedRoute: ActivatedRoute,
        private applicationFacade: ApplicationFacade,
        private otpService: OpenTripPlannerService,
        private mapApi: MapApiService,
        private router: Router,
        private location: Location,
        private translate: TranslateService,
        private printScheduleService: PrintScheduleService,
        public openTripPlannerFacade: OpenTripPlannerFacade,
        @Inject('ENV') public env: Env,
        private favoritesFacade: FavoritesFacade,
        public network: NetworkService,
        public backNavService: BackNavigationService,
        private patternScheduleShared: PatternScheduleSharedService
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscribeToRouteData();
        this.favoritesFacade.getFavoriteSchedules();

        this.favoriteSchedules$.pipe(takeUntil(this.componentDestroyed$)).subscribe((schedules) => {
            this.allFavoriteSchedules = schedules;

            if (this.favoriteStopSchedule) {
                this.favoriteStopSchedule =
                    this.allFavoriteSchedules.find(
                        (s) =>
                            (s.stopId === this.favoriteStopSchedule.stopId &&
                                s.routeId === this.favoriteStopSchedule.routeId) ||
                            (s.id && this.favoriteStopSchedule.id === s.id)
                    ) || this.favoriteStopSchedule;
            }

            this.rebuildActionsMenu();
        });

        if (this.env.APP_NAME === 'peutk') {
            this.rebuildActionsMenu();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.openTripPlannerFacade.setRoutePatternStop(null);
        this.openTripPlannerFacade.clearExploreMapStops();
    }

    navigateBack(): void {
        this.backNavService.goBack('explore');
    }

    changeTab(tabType: TabType) {
        this.tabType = tabType;
        this.setStopTimes();
    }

    getDateFormat(): string {
        return this.env.PRIMARY_DATE_FORMAT_NGPIPE;
    }

    isFavoriteScheduleAlreadyExist(): boolean {
        if (!this.params.patternId) {
            return false;
        }

        const favoriteSchedule: FavoriteSchedule = {
            route: this.scheduleRoute,
            routeId: this.scheduleRoute?.id,
            stop: this.scheduleStop,
            stopId: this.scheduleStop?.id,
            patternId: this.params.patternId
        };
        return this.isFavoriteScheduleExist(favoriteSchedule);
    }

    rebuildActionsMenu(): void {
        this.actions = [];

        if (!this.env.EXPLORE?.HIDE_FAVORITE_SCHEDULE_ADDITIONAL_INFO) {
            const menuActionAdditionalInfo = { title: 'ADDITIONAL_INFO', type: ActionType.ADDITIONAL_INFO };

            this.actions.push(menuActionAdditionalInfo);
        }

        if (this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE) {
            const removeOrAddAction = this.isFavoriteScheduleAlreadyExist()
                ? { title: 'REMOVE_FROM_FAVORITES', type: ActionType.REMOVE_FROM_FAVORITES }
                : { title: 'SAVE_AS_FAVORITE', type: ActionType.SAVE_AS_FAVORITE };

            this.actions.push(removeOrAddAction);
        }

        if (!this.env.EXPLORE?.HIDE_FAVORITE_SCHEDULE_PRINT) {
            const menuActionPrintSchedule = { title: 'PRINT_SCHEDULE', type: ActionType.PRINT_SCHEDULE };

            this.actions.push(menuActionPrintSchedule);
        }
    }

    handleClickFavoriteIcon(): void {
        if (!this.network.getCurrentStatus()) {
            return;
        }

        if (this.isFavoriteScheduleAlreadyExist()) {
            this.favoritesFacade.deleteFavoriteSchedules([this.favoriteStopSchedule]);
        } else {
            this.favoritesFacade.addFavoriteSchedule(this.favoriteStopSchedule);
        }
        this.rebuildActionsMenu();
    }

    setStopTimes() {
        const scheduleDate = this.getScheduleDate();
        if (!scheduleDate) {
            this.times = [];
            return;
        }

        if (!this.schedulePattern?.id) {
            return;
        }

        this.otpService
            .getPatternScheduleOnStop({
                pattern: this.schedulePattern,
                stopId: this.scheduleStop.id,
                scheduleDate
            })
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((times) => {
                this.times = times;
            });
    }

    isWatchingTodaySchedule(): boolean {
        if (this.tabType === TabType.TODAY) {
            return true;
        }
        if (this.tabType === TabType.DATE && this.setDateValue?.datetime) {
            return moment(this.setDateValue?.datetime).isSame(moment(), 'days');
        }
        return false;
    }

    async handleAction(action): Promise<void> {
        switch (action.type) {
            case ActionType.ADDITIONAL_INFO:
                this.patternScheduleShared.setRoute(this.scheduleRoute);
                await this.router.navigate([
                    this.translate.currentLang,
                    'explore',
                    'pattern',
                    this.params.patternId,
                    'schedule',
                    'stop',
                    this.params.stopId,
                    'additional-info'
                ]);
                break;
            case ActionType.SAVE_AS_FAVORITE:
                this.favoritesFacade.addFavoriteSchedule(this.favoriteStopSchedule);
                break;

            case ActionType.REMOVE_FROM_FAVORITES:
                this.favoritesFacade.deleteFavoriteSchedules([this.favoriteStopSchedule]);
                break;

            case ActionType.PRINT_SCHEDULE:
                this.printScheduleService
                    .getHtmlStopSchedule({
                        routeId: this.scheduleRoute.id,
                        stopId: this.scheduleStop.id,
                        patternId: this.params.patternId,
                        date: this.getScheduleDate() ?? undefined
                    })
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe((html) => {
                        this.printScheduleService.printHTML(html);
                    });
                break;
            default:
                break;
        }
    }

    private subscribeToRouteData() {
        combineLatest([this.activatedRoute.queryParamMap, this.activatedRoute.paramMap])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([queries, params]) => {
                const patternId = params.get('patternId');
                const stopId = params.get('stopId');
                const scheduleId = queries.get('scheduleId');

                this.params = { patternId, stopId };

                forkJoin([this.otpService.getPattern(patternId), this.otpService.getStop(stopId)])
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(([pattern, stop]) => {
                        this.openTripPlannerFacade.setRoutePatternStop(stop);
                        this.otpService
                            .getRoute(pattern.routeId)
                            .pipe(takeUntil(this.componentDestroyed$))
                            .subscribe((route) => {
                                this.scheduleRoute = route;
                                this.schedulePattern = pattern;
                                this.schedulePatternTitle = OtpUtil.getPatternDisplayLabel(
                                    pattern,
                                    this.env.EXPLORE?.PATTERN_LABEL_PREFER_HEADSIGN === true
                                );
                                this.scheduleStop = stop;
                                this.favoriteStopSchedule = {
                                    id: scheduleId,
                                    route: this.scheduleRoute,
                                    routeId: this.scheduleRoute.id,
                                    stop: this.scheduleStop,
                                    stopId: this.scheduleStop.id,
                                    patternId: this.params.patternId
                                };

                                if (this.allFavoriteSchedules?.length) {
                                    this.favoriteStopSchedule =
                                        this.allFavoriteSchedules.find(
                                            (s) =>
                                                (s.stopId === this.favoriteStopSchedule.stopId &&
                                                    s.routeId === this.favoriteStopSchedule.routeId) ||
                                                (this.favoriteStopSchedule.id && this.favoriteStopSchedule.id === s.id)
                                        ) || this.favoriteStopSchedule;
                                }

                                this.openTripPlannerFacade.setExploreMapStops([stop]);
                                this.setStopTimes();
                                this.centerMapToStop();
                                this.rebuildActionsMenu();
                            });
                    });
            });
    }

    private getScheduleDate(): string | null {
        switch (this.tabType) {
            case 'today':
                return moment().format('YYYY-MM-DD');
            case 'tomorrow':
                return moment().add(1, 'days').format('YYYY-MM-DD');
            case 'date':
                return this.setDateValue?.datetime ? this.setDateValue.datetime : null;
        }
    }

    private centerMapToStop(): void {
        this.mapApi
            .mapReady()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((ready) => {
                if (ready) {
                    const latLng: LatLng = { lat: this.scheduleStop.lat, lng: this.scheduleStop.lon };
                    this.mapApi.centerMapToCoordinates(latLng);
                }
            });
    }

    public isFavoriteScheduleExist(schedule: FavoriteSchedule): boolean {
        const { route, stop } = schedule;
        const schedules = this.allFavoriteSchedules;

        return schedules.some(
            (el) => (el.route?.id === route?.id && el.stop?.id === stop?.id) || schedule?.id === el?.id
        );
    }
}
